/**
 * LoveLink V1 Earnings Panel
 * Shows Net-30 split: pending hold, available, and paid earnings
 */
import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Loader, ArrowDownRight, TrendingUp, Clock, CheckCircle, DollarSign } from 'lucide-react';

type LoveLinkEarning = {
  id: string;
  created_at: string;
  commission_amount_cents: number;
  amount_total_cents: number;
  status: 'pending' | 'paid';
  is_available: boolean;
  product_label: string;
  invitee_email: string;
  currency: string;
};

type LoveLinkEarningsSummary = {
  total_cents: number;
  pending_hold_cents: number;
  available_cents: number;
  paid_cents: number;
  recent_earnings: LoveLinkEarning[];
};

const centsToDollars = (cents: number | undefined | null): string => {
  if (!cents) return '$0.00';
  return `$${(cents / 100).toFixed(2)}`;
};

const LoveLinkEarningsPanel: React.FC = () => {
  const { token } = useContext(AuthContext);
  const [data, setData] = useState<LoveLinkEarningsSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;

    const fetchEarnings = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await axios.get('/api/referrals/earnings/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (err: any) {
        // Silence 403/401 errors - user may not have referral access
        const status = err?.response?.status;
        if (status === 403 || status === 401) {
          console.info('[LoveLink] Earnings not available for this user (expected)');
        } else {
          console.error('Error fetching LoveLink earnings', err);
        }
        setError('Unable to load rewards at the moment.');
      } finally {
        setLoading(false);
      }
    };

    fetchEarnings();
  }, [token]);

  if (!token) {
    return null;
  }

  return (
    <section className="mt-10">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-gray-900">
            LoveLink Overview
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Build recurring value with the people you invite intentionally.
          </p>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-10">
          <Loader className="w-6 h-6 animate-spin text-emerald-600" />
        </div>
      )}

      {error && !loading && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* Recent earnings table - Summary cards removed to avoid duplication with parent page */}
          <div className="mt-8 surface-card rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Recent Rewards
              </h3>
              <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                <ArrowDownRight className="w-4 h-4 mr-1" />
                Newest first
              </div>
            </div>

            {(!data.recent_earnings || data.recent_earnings.length === 0) && (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-slate-800 mb-4">
                  <DollarSign className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-sm text-gray-600 mb-2">No rewards yet</p>
                <p className="text-xs text-gray-500">
                  Share your LoveLink from the Dashboard to start building your Circle and receiving 10%.
                </p>
              </div>
            )}

            {data.recent_earnings && data.recent_earnings.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-3 pr-4 font-semibold">Date</th>
                      <th className="py-3 pr-4 font-semibold">From</th>
                      <th className="py-3 pr-4 font-semibold">Product</th>
                      <th className="py-3 pr-4 text-right font-semibold">Amount</th>
                      <th className="py-3 pl-4 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.recent_earnings.map((e) => {
                      let date: Date;
                      try {
                        date = new Date(e.created_at);
                      } catch {
                        date = new Date();
                      }
                      
                      const dateLabel = isNaN(date.getTime())
                        ? e.created_at
                        : date.toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          });

                      const statusLabel =
                        e.status === 'paid'
                          ? 'Paid'
                          : e.is_available
                          ? 'Available'
                          : 'In 30-day hold';

                      const statusColor =
                        e.status === 'paid'
                          ? 'text-indigo-700 bg-indigo-50 border-indigo-200'
                          : e.is_available
                          ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                          : 'text-amber-700 bg-amber-50 border-amber-200';

                      return (
                        <tr key={e.id} className="hover:bg-gray-50 transition-colors">
                          <td className="py-3 pr-4 text-gray-700">{dateLabel}</td>
                          <td className="py-3 pr-4 text-gray-700 truncate max-w-[150px]">
                            {e.invitee_email || 'Referred user'}
                          </td>
                          <td className="py-3 pr-4 text-gray-700">
                            {e.product_label || 'Purchase'}
                          </td>
                          <td className="py-3 pr-4 text-right font-semibold text-gray-900">
                            {centsToDollars(e.commission_amount_cents)}
                          </td>
                          <td className="py-3 pl-4">
                            <span
                              className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusColor}`}
                            >
                              {statusLabel}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default LoveLinkEarningsPanel;
